import { CHART_PALETTE, ROCELL_COLORS } from './rocellTheme';

export const formatTimeSlotLabel = (start, end) => {
  return `${(start || '').trim()} – ${(end || '').trim()}`;
};

export const getBarChartHeight = (categoryCount, horizontal = false) => {
  if (horizontal) {
    return Math.max(420, categoryCount * 36 + 120);
  }
  return Math.max(460, 380 + Math.max(0, categoryCount - 6) * 12);
};

const baseChart = {
  fontFamily: 'inherit',
  toolbar: { show: true, tools: { download: true, selection: false, zoom: false, zoomin: false, zoomout: false, pan: false, reset: false } },
  animations: { enabled: true },
};

export const buildGroupedBarChartOptions = (categories, overrides = {}) => {
  const count = categories.length;
  const needsRotation = count > 3;

  return {
    chart: {
      ...baseChart,
      type: 'bar',
      stacked: false,
      height: getBarChartHeight(count),
    },
    colors: [ROCELL_COLORS.navy, ROCELL_COLORS.primary],
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: count > 10 ? '90%' : '70%',
        borderRadius: 4,
        dataLabels: { position: 'top' },
      },
    },
    dataLabels: {
      enabled: true,
      offsetY: -18,
      hideOverflowingLabels: true,
      style: {
        fontSize: '11px',
        fontWeight: 600,
        colors: [ROCELL_COLORS.navy],
      },
      background: { enabled: false },
      formatter(val, opts) {
        const series = opts.w.config.series;
        const dataPointIndex = opts.dataPointIndex;
        let total = 0;
        series.forEach((s) => {
          total += s.data[dataPointIndex] || 0;
        });
        const percentage = total > 0 ? ((val / total) * 100).toFixed(1) : 0;
        return val > 0 ? `${val} (${percentage}%)` : '';
      },
    },
    legend: {
      position: 'top',
      horizontalAlign: 'left',
      fontSize: '13px',
      markers: { width: 10, height: 10, radius: 2 },
      itemMargin: { horizontal: 12, vertical: 0 },
    },
    xaxis: {
      categories,
      tickPlacement: 'on',
      labels: {
        rotate: needsRotation ? -45 : 0,
        rotateAlways: needsRotation,
        hideOverlappingLabels: false,
        trim: false,
        minHeight: needsRotation ? 100 : 48,
        style: { fontSize: '11px', colors: ROCELL_COLORS.navy },
      },
      axisBorder: { show: true, color: '#e4e6ef' },
      axisTicks: { show: true, color: '#e4e6ef' },
    },
    yaxis: {
      labels: {
        style: { fontSize: '11px', colors: ROCELL_COLORS.muted },
      },
      title: {
        style: { fontSize: '12px', color: ROCELL_COLORS.navy, fontWeight: 600 },
      },
    },
    grid: {
      borderColor: '#eef0f4',
      padding: { left: 4, right: 8, bottom: needsRotation ? 8 : 0 },
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter(val, opts) {
          const series = opts.w.config.series;
          const dataPointIndex = opts.dataPointIndex;
          let total = 0;
          series.forEach((s) => {
            total += s.data[dataPointIndex] || 0;
          });
          const percentage = total > 0 ? ((val / total) * 100).toFixed(1) : 0;
          return `${val} (${percentage}%)`;
        },
      },
    },
    ...overrides,
  };
};

export const buildHorizontalBarChartOptions = (categories, maxValue = 0, overrides = {}) => {
  const count = categories.length;
  const dataMax = maxValue > 0 ? maxValue : 100;
  const axisMax = Math.max(100, Math.ceil((dataMax * 1.35) / 10) * 10);

  return {
    chart: {
      ...baseChart,
      type: 'bar',
      height: getBarChartHeight(count, true),
    },
    colors: [ROCELL_COLORS.primary],
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: '75%',
        borderRadius: 4,
        dataLabels: {
          position: 'top',
        },
      },
    },
    dataLabels: {
      enabled: true,
      offsetX: 36,
      textAnchor: 'start',
      style: {
        fontSize: '11px',
        fontWeight: 600,
        colors: [ROCELL_COLORS.navy],
      },
      formatter(val) {
        return val > 0 ? `${Number(val).toFixed(1)}%` : '';
      },
    },
    legend: { show: false },
    xaxis: {
      categories,
      max: axisMax,
      labels: {
        style: { fontSize: '11px', colors: ROCELL_COLORS.muted },
        formatter(val) {
          return `${Number(val).toFixed(0)}%`;
        },
      },
    },
    yaxis: {
      labels: {
        style: { fontSize: '11px', colors: ROCELL_COLORS.navy },
        maxWidth: 140,
      },
    },
    grid: {
      borderColor: '#eef0f4',
      padding: { left: 8, right: 96, top: 4, bottom: 4 },
    },
    tooltip: {
      y: {
        formatter(val) {
          return `${Number(val).toFixed(2)}%`;
        },
      },
    },
    ...overrides,
  };
};

export const buildLineChartOptions = (categories, overrides = {}) => ({
  chart: {
    ...baseChart,
    type: 'line',
      height: 460,
    zoom: { enabled: false },
  },
  colors: [ROCELL_COLORS.primary],
  stroke: { curve: 'smooth', width: 3 },
  markers: { size: 5, strokeWidth: 2, hover: { size: 7 } },
  dataLabels: { enabled: false },
  legend: { show: false },
  xaxis: {
    categories,
    title: {
      text: 'Month',
      style: { fontSize: '12px', color: ROCELL_COLORS.navy, fontWeight: 600 },
    },
    labels: {
      rotate: -25,
      rotateAlways: categories.length > 2,
      style: { fontSize: '11px', colors: ROCELL_COLORS.navy },
    },
  },
  yaxis: {
    title: {
      text: 'Cost (Rs.)',
      style: { fontSize: '12px', color: ROCELL_COLORS.navy, fontWeight: 600 },
    },
    labels: {
      style: { fontSize: '11px', colors: ROCELL_COLORS.muted },
      formatter(val) {
        return Number(val).toLocaleString('en-LK', { maximumFractionDigits: 0 });
      },
    },
  },
  grid: {
    borderColor: '#eef0f4',
    padding: { left: 12, right: 16 },
  },
  tooltip: {
    y: {
      formatter(val) {
        return `Rs. ${Number(val).toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      },
    },
  },
  ...overrides,
});

export const buildPieChartOptions = (labels, overrides = {}) => ({
    chart: {
      ...baseChart,
      type: 'donut',
      height: 440,
    },
  labels,
  colors: CHART_PALETTE,
  legend: {
    position: 'bottom',
    horizontalAlign: 'center',
    fontSize: '12px',
    showForSingleSeries: true,
    markers: { width: 10, height: 10, radius: 2 },
    itemMargin: { horizontal: 8, vertical: 4 },
  },
  plotOptions: {
    pie: {
      donut: {
        size: '52%',
        labels: {
          show: true,
          name: { fontSize: '13px', color: ROCELL_COLORS.navy },
          value: {
            fontSize: '14px',
            fontWeight: 600,
            color: ROCELL_COLORS.navy,
            formatter(val) {
              return Number(val).toLocaleString('en-LK');
            },
          },
          total: {
            show: true,
            label: 'Total',
            fontSize: '13px',
            color: ROCELL_COLORS.muted,
            formatter(w) {
              return w.globals.seriesTotals.reduce((a, b) => a + b, 0).toLocaleString('en-LK');
            },
          },
        },
      },
    },
  },
  dataLabels: {
    enabled: true,
    dropShadow: { enabled: false },
    style: { fontSize: '11px', fontWeight: 600 },
    formatter(val) {
      return `${Number(val).toFixed(1)}%`;
    },
  },
  tooltip: {
    y: {
      formatter(val) {
        return Number(val).toLocaleString('en-LK');
      },
    },
  },
  ...overrides,
});
